import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Compass, Navigation, MapPin, Mountain, Check, Download } from 'lucide-react';
import usePageMeta from '../hooks/usePageMeta';

const Section = ({ children, className = '' }) => (
  <section className={`max-w-[1400px] mx-auto px-6 lg:px-10 ${className}`}>{children}</section>
);

// ---------- Logo variations ----------

// V1: Current default — navy square W + wordmark, slate-blue dot
const V1 = ({ compact = false }) => (
  <div className="flex items-center gap-3">
    <div className="relative w-11 h-11 rounded-sm bg-[#0F2A44] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A5F] to-[#0F2A44]" />
      <span className="relative text-white font-serif text-2xl leading-none">W</span>
      <span className="absolute bottom-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-[#7DB4D8]" />
    </div>
    <div className="leading-[1.05]">
      <div className="font-serif text-[26px] text-[#0F2A44] tracking-[-0.01em]">Waypoint</div>
      <div className="text-[10px] uppercase tracking-[0.285em] text-slate-500 mt-0.5">Transformation</div>
    </div>
  </div>
);

// V2: Yellow dot accent
const V2 = () => (
  <div className="flex items-center gap-3">
    <div className="relative w-11 h-11 rounded-sm bg-[#0F2A44] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A5F] to-[#0F2A44]" />
      <span className="relative text-white font-serif text-2xl leading-none">W</span>
      <span className="absolute bottom-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-[#FFC629]" />
    </div>
    <div className="leading-[1.05]">
      <div className="font-serif text-[26px] text-[#0F2A44] tracking-[-0.01em]">Waypoint</div>
      <div className="text-[10px] uppercase tracking-[0.285em] text-slate-500 mt-0.5">Transformation</div>
    </div>
  </div>
);

// V2 mark only — for download previews
const V2Mark = () => (
  <div className="relative w-16 h-16 rounded-sm bg-[#0F2A44] flex items-center justify-center overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A5F] to-[#0F2A44]" />
    <span className="relative text-white font-serif text-4xl leading-none">W</span>
    <span className="absolute bottom-2 right-2 w-2 h-2 rounded-full bg-[#FFC629]" />
  </div>
);

// V3: Compass icon in place of W, slate-blue dot
const V3 = () => (
  <div className="flex items-center gap-3">
    <div className="relative w-11 h-11 rounded-sm bg-[#0F2A44] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A5F] to-[#0F2A44]" />
      <Compass className="relative w-5 h-5 text-white" strokeWidth={1.5} />
      <span className="absolute bottom-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-[#7DB4D8]" />
    </div>
    <div className="leading-[1.05]">
      <div className="font-serif text-[26px] text-[#0F2A44] tracking-[-0.01em]">Waypoint</div>
      <div className="text-[10px] uppercase tracking-[0.285em] text-slate-500 mt-0.5">Transformation</div>
    </div>
  </div>
);

// V4: Pure wordmark — no icon block
const V4 = () => (
  <div className="leading-[1] flex flex-col items-start">
    <div className="font-serif text-[34px] text-[#0F2A44] tracking-[-0.015em] flex items-baseline">
      Waypoint
      <span className="w-1.5 h-1.5 rounded-full bg-[#3E6A94] ml-1 mb-1" />
    </div>
    <div className="text-[11px] uppercase tracking-[0.35em] text-slate-500 mt-2">Transformation</div>
  </div>
);

// V5: Two-tone Waypoint
const V5 = () => (
  <div className="flex items-center gap-3">
    <div className="relative w-11 h-11 rounded-sm bg-[#0F2A44] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A5F] to-[#0F2A44]" />
      <span className="relative text-white font-serif text-2xl leading-none">W</span>
      <span className="absolute bottom-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-[#FFC629]" />
    </div>
    <div className="leading-[1.05]">
      <div className="font-serif text-[26px] tracking-[-0.01em]">
        <span className="text-[#0F2A44]">Way</span>
        <span className="text-[#3E6A94]">point</span>
      </div>
      <div className="text-[10px] uppercase tracking-[0.285em] text-slate-500 mt-0.5">Transformation</div>
    </div>
  </div>
);

// V6: Mountain/summit icon (waypoint reached) + yellow dot
const V6 = () => (
  <div className="flex items-center gap-3">
    <div className="relative w-11 h-11 rounded-sm bg-[#0F2A44] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A5F] to-[#0F2A44]" />
      <Mountain className="relative w-5 h-5 text-white" strokeWidth={1.5} />
      <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-[#FFC629]" />
    </div>
    <div className="leading-[1.05]">
      <div className="font-serif text-[26px] text-[#0F2A44] tracking-[-0.01em]">Waypoint</div>
      <div className="text-[10px] uppercase tracking-[0.285em] text-slate-500 mt-0.5">Transformation</div>
    </div>
  </div>
);

// V7: All-caps Waypoint (uniform width with Transformation)
const V7 = () => (
  <div className="flex items-center gap-3">
    <div className="relative w-11 h-11 rounded-sm bg-[#0F2A44] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A5F] to-[#0F2A44]" />
      <Navigation className="relative w-5 h-5 text-white" strokeWidth={1.5} />
      <span className="absolute bottom-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-[#7DB4D8]" />
    </div>
    <div className="leading-[1.05]">
      <div className="text-[17px] uppercase tracking-[0.24em] font-semibold text-[#0F2A44]">Waypoint</div>
      <div className="text-[10px] uppercase tracking-[0.285em] text-slate-500 mt-1">Transformation</div>
    </div>
  </div>
);

// V8: Slate accent, minimal square
const V8 = () => (
  <div className="flex items-center gap-3">
    <div className="relative w-11 h-11 border-2 border-[#0F2A44] flex items-center justify-center overflow-hidden bg-white">
      <span className="text-[#0F2A44] font-serif text-2xl leading-none">W</span>
      <span className="absolute bottom-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-[#FFC629]" />
    </div>
    <div className="leading-[1.05]">
      <div className="font-serif text-[26px] text-[#0F2A44] tracking-[-0.01em]">Waypoint</div>
      <div className="text-[10px] uppercase tracking-[0.285em] text-slate-500 mt-0.5">Transformation</div>
    </div>
  </div>
);

const VARIANTS = [
  { id: 'v1', name: 'Original — slate-blue dot', desc: 'Navy W tile with slate-blue accent dot. Waypoint sized to match Transformation width.', Component: V1 },
  { id: 'v2', name: 'Yellow dot', desc: 'Same as current, but the accent dot is a bright, warm yellow.', Component: V2, isCurrent: true },
  { id: 'v3', name: 'Compass icon', desc: 'A compass replaces the W — reinforces "waypoint" as navigation.', Component: V3 },
  { id: 'v4', name: 'Pure wordmark', desc: 'No icon block. A confident, editorial serif with a small punctuating dot.', Component: V4 },
  { id: 'v5', name: 'Two-tone Waypoint + yellow', desc: '"Way" in navy, "point" in slate-blue. Yellow accent dot.', Component: V5 },
  { id: 'v6', name: 'Mountain summit + yellow', desc: 'A summit icon (waypoint reached) with a bright yellow beacon.', Component: V6 },
  { id: 'v7', name: 'All-caps + navigation', desc: 'Waypoint set in caps so both words share the same visual weight.', Component: V7 },
  { id: 'v8', name: 'Outlined tile + yellow', desc: 'A lighter, outlined W tile for a more editorial feel.', Component: V8 },
];

export default function LogoPreview() {
  usePageMeta({
    title: 'Logo Studio',
    description: 'Internal logo studio.',
    path: '/logos',
    noindex: true,
  });
  const [pick, setPick] = useState(null);

  return (
    <div>
      <section className="bg-[#F5F7FA] border-b border-slate-200">
        <Section className="pt-20 pb-14">
          <div className="text-[11px] uppercase tracking-[0.24em] text-slate-500 mb-6">Logo studio</div>
          <h1 className="font-serif text-4xl lg:text-6xl leading-[1.05] tracking-[-0.02em] text-[#0F2A44] max-w-4xl text-balance">
            Pick a direction.
          </h1>
          <p className="mt-6 text-slate-600 leading-relaxed max-w-2xl">
            Eight variations to choose from. Click any option to preview it "in situ" on a mock navigation bar below. Tell me which one you like and I'll set it as the site default.
          </p>
        </Section>
      </section>

      <Section className="py-16">
        <div className="grid md:grid-cols-2 gap-6">
          {VARIANTS.map((v) => {
            const selected = pick === v.id;
            return (
              <button
                key={v.id}
                onClick={() => setPick(v.id)}
                className={`text-left border p-8 lg:p-10 transition-all rounded-sm ${selected ? 'border-[#0F2A44] bg-white shadow-lg' : 'border-slate-200 bg-white hover:border-[#0F2A44] hover:shadow-md'}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-1">Variation {v.id.toUpperCase()}</div>
                    <div className="font-serif text-xl text-[#0F2A44]">{v.name}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {v.isCurrent && <span className="text-[10px] uppercase tracking-[0.16em] text-[#3E6A94] bg-[#3E6A94]/10 px-2 py-1 rounded-sm">In use</span>}
                    {selected && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] text-white bg-[#0F2A44] px-2 py-1 rounded-sm"><Check className="w-3 h-3" /> Selected</span>}
                  </div>
                </div>
                <div className="h-32 flex items-center justify-center bg-[#F5F7FA] border border-slate-200 rounded-sm mb-4">
                  <v.Component />
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">{v.desc}</p>
              </button>
            );
          })}
        </div>
      </Section>

      {/* Preview strip */}
      {pick && (
        <section className="bg-[#0F2A44] text-white">
          <Section className="py-14">
            <div className="text-[11px] uppercase tracking-[0.24em] text-[#7DB4D8] mb-3">Preview on a dark surface</div>
            <div className="font-serif text-2xl mb-8">How it looks on the footer background</div>
            <div className="bg-[#0F2A44] border border-white/10 p-10 rounded-sm flex items-center">
              <div className="inline-flex items-center bg-white px-4 py-3 rounded-sm">
                {(() => {
                  const V = VARIANTS.find((x) => x.id === pick).Component;
                  return <V />;
                })()}
              </div>
            </div>
          </Section>
        </section>
      )}

      {pick && (
        <Section className="py-14 text-center">
          <div className="text-sm text-slate-600 mb-4">Like this one?</div>
          <div className="font-serif text-2xl text-[#0F2A44] mb-6">Reply with "use {pick.toUpperCase()}" and I'll set it as the site default.</div>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 border border-[#0F2A44] text-[#0F2A44] text-sm hover:bg-[#0F2A44] hover:text-white transition-colors">Back to home</Link>
        </Section>
      )}

      {/* Downloads */}
      <section className="bg-[#F5F7FA] border-t border-slate-200">
        <Section className="py-20 lg:py-24">
          <div className="text-[11px] uppercase tracking-[0.24em] text-slate-500 mb-4">Brand assets</div>
          <h2 className="font-serif text-4xl lg:text-5xl leading-[1.05] text-[#0F2A44] tracking-tight mb-4">Download the logo.</h2>
          <p className="text-slate-600 leading-relaxed max-w-2xl mb-12">
            Vector SVG files of the active V2 mark (yellow dot). Scalable to any size — use them in decks, documents, email signatures, and social profiles.
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              { file: 'waypoint-logo-mark.svg', title: 'Logo mark', desc: 'The W tile alone. Best for favicons, avatars, and app icons.', preview: 'mark', bg: 'transparent' },
              { file: 'waypoint-logo-mark-onwhite.svg', title: 'Logo mark · on white', desc: 'Same mark centered on a white canvas. Ideal for printing.', preview: 'mark', bg: 'white' },
              { file: 'waypoint-logo-full.svg', title: 'Full logo with name', desc: 'Mark + Waypoint Transformation wordmark. The primary lockup.', preview: 'full', bg: 'transparent' },
              { file: 'waypoint-logo-full-onwhite.svg', title: 'Full logo · on white', desc: 'Full lockup on a white canvas. Best for slides and docs.', preview: 'full', bg: 'white' },
            ].map((d) => (
              <div key={d.file} className="border border-slate-200 bg-white rounded-sm overflow-hidden">
                <div className={`h-40 flex items-center justify-center ${d.bg === 'white' ? 'bg-white border-b border-slate-100' : 'bg-[#F5F7FA] border-b border-slate-100'}`}>
                  {d.preview === 'mark' ? <V2Mark /> : <V2 />}
                </div>
                <div className="p-6 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-serif text-lg text-[#0F2A44]">{d.title}</div>
                    <p className="text-sm text-slate-600 mt-1 leading-relaxed">{d.desc}</p>
                    <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400 mt-3">SVG · Vector</div>
                  </div>
                  <a href={`/downloads/${d.file}`} download className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#0F2A44] text-white text-sm hover:bg-[#1E3A5F] transition-colors rounded-sm flex-shrink-0">
                    <Download className="w-4 h-4" /> Download
                  </a>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 text-xs text-slate-500 max-w-2xl leading-relaxed">
            Colors: Navy <code className="text-[#0F2A44] font-mono">#0F2A44</code> → <code className="text-[#0F2A44] font-mono">#1E3A5F</code> (gradient) · Accent <code className="text-[#0F2A44] font-mono">#FFC629</code> · Wordmark uses Fraunces (serif) with Georgia fallback.
          </div>
        </Section>
      </section>
    </div>
  );
}
